<template>
  <div :class="cn('tw:block', $attrs.class as any)" :style="$attrs.style as any">
    <label
      v-if="label != null"
      :id="labelId"
      :for="textareaId"
      class="tw:mb-1.5 tw:block tw:text-13 tw:font-medium tw:text-gray-700">
      {{ label }}
      <span v-if="required" aria-hidden="true" class="tw:text-red-600">&nbsp;*</span>
    </label>

    <textarea
      :id="textareaId"
      ref="textareaRef"
      :rows="rows"
      v-bind="forwardedAttrs"
      :value="modelValue"
      :disabled="disabled"
      :aria-invalid="invalid || undefined"
      :aria-describedby="messageId"
      :class="cn(CONTROL_BASE, 'tw:h-auto tw:min-h-14 tw:resize-y', invalid && CONTROL_INVALID)"
      @input="onInput"
      @change="onChange"></textarea>

    <!-- error takes precedence over hint; several errors render as a list -->
    <div v-if="errorList.length > 0" :id="messageId" class="tw:mt-1.5 tw:text-13 tw:font-normal tw:text-red-600">
      <span v-if="errorList.length === 1">{{ errorList[0] }}</span>
      <ul v-else class="tw:list-disc tw:pl-4">
        <li v-for="error in errorList" :key="error">{{ error }}</li>
      </ul>
    </div>
    <span v-else-if="$slots.hint" :id="messageId" class="tw:mt-1.5 tw:block tw:text-13 tw:font-normal tw:text-gray-600">
      <slot name="hint" />
    </span>
  </div>
</template>

<script setup lang="ts">
// mirrors the LEAD Design System <Textarea>, which the DS keeps separate from <Input>; class strings
// taken from its Textarea.tsx. Fixed size, resizable, no autosize or affixes. Field composition
// mirrors LeadInput, but the label renders as plain text rather than v-html.
import { computed, ref, useAttrs, useId } from 'vue';
import { cn } from '@/lib/cn';
import { CONTROL_BASE, CONTROL_INVALID } from './controlStyles';

defineOptions({ inheritAttrs: false });

interface Props {
  modelValue?: string | number | null;
  rows?: number;
  label?: string;
  errors?: string[] | string | null;
  required?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  rows: 2,
  label: undefined,
  errors: null,
  required: false,
  disabled: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  change: [event: Event];
}>();

const attrs = useAttrs();
const slots = defineSlots<{
  hint?: () => any;
}>();

/* --------------------------------- STATE ---------------------------------- */

const textareaRef = ref<HTMLTextAreaElement | null>(null);
const generatedId = useId();
const textareaId = computed(() => (attrs.id as string) || `lead-textarea-${generatedId}`);
const labelId = computed(() => (props.label != null ? `${textareaId.value}-label` : undefined));

/* --------------------------------- COMPUTED ------------------------------- */

// class/style/id belong to the wrapper; everything else (including `rows`) falls through to <textarea>
const forwardedAttrs = computed(() => {
  const { class: _c, style: _s, id: _i, ...rest } = attrs as any;
  return rest;
});

const errorList = computed<string[]>(() => {
  if (props.errors == null) return [];
  return Array.isArray(props.errors) ? props.errors.filter(Boolean) : [props.errors].filter(Boolean);
});
const invalid = computed(() => errorList.value.length > 0);

const messageId = computed(() => {
  if (errorList.value.length > 0 || slots.hint) return `${textareaId.value}-message`;
  return undefined;
});

/* --------------------------------- METHODS -------------------------------- */

const onInput = (event: Event) => emit('update:modelValue', (event.target as HTMLTextAreaElement).value);
const onChange = (event: Event) => emit('change', event);

defineExpose({ focus: () => textareaRef.value?.focus(), textareaRef });
</script>
